// CRP Access Authorization Modal - Updated for Vocational Rehabilitation Services
import React, { useState, useEffect } from "react";

const PHIAccessModal = ({
  onConfirm,
  onCancel,
  entityType = "consumer record",
  entityId = "",
}) => {
  // 🔧 Use localStorage to persist form data during session events
  const getStoredFormData = () => {
    try {
      const stored = localStorage.getItem("crp_access_form_data");
      return stored
        ? JSON.parse(stored)
        : {
            reason: "",
            purpose: "vocational_services",
            emergencyAccess: false,
            acknowledgment: false,
          };
    } catch (error) {
      console.warn("Could not load stored CRP access form data:", error);
      return {
        reason: "",
        purpose: "vocational_services",
        emergencyAccess: false,
        acknowledgment: false,
      };
    }
  };

  const [formData, setFormData] = useState(getStoredFormData);
  const [errors, setErrors] = useState({});

  // 🔧 Save form data to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem("crp_access_form_data", JSON.stringify(formData));
    } catch (error) {
      console.warn("Could not save CRP access form data:", error);
    }
  }, [formData]);

  // 🔧 Clear stored data when modal is successfully submitted
  const clearStoredData = () => {
    try {
      localStorage.removeItem("crp_access_form_data");
    } catch (error) {
      console.warn("Could not clear stored CRP access form data:", error);
    }
  };

  // ✅ Vocational Rehabilitation & Life Skills Purpose Descriptions
  const purposeDescriptions = {
    // Core VR Services
    vocational_services:
      "Providing vocational rehabilitation and employment services",
    life_skills_training:
      "Life skills development and independent living support",
    employment_support: "Job coaching, placement, and retention services",
    assessment_evaluation: "Vocational assessment and skills evaluation",

    // Training & Development
    work_adjustment: "Work adjustment and skills training programs",
    community_integration: "Community-based training and integration services",
    career_development: "Career exploration and development planning",
    transition_services: "Transition services for youth and adults",

    // Support Services
    case_management: "Case coordination and individualized service planning",
    progress_monitoring: "Service progress monitoring and outcome tracking",
    family_coordination: "Family and caregiver coordination",
    advocacy_support: "Consumer advocacy and rights protection",

    // Administrative Functions
    compliance_review: "Program compliance and quality assurance review",
    reporting: "Required regulatory or compliance reporting",
    audit: "Internal audit or program evaluation activities",
    training: "Staff training and professional development",

    // Emergency Access
    emergency: "Emergency access for immediate service needs",
    crisis_intervention: "Crisis intervention and emergency support",
  };

  // ✅ Purpose groups organized for VR/CRP services
  const purposeGroups = {
    "Core Vocational Services": {
      vocational_services: "Vocational Services",
      employment_support: "Employment Support",
      life_skills_training: "Life Skills Training",
      assessment_evaluation: "Assessment & Evaluation",
    },
    "Training & Development": {
      work_adjustment: "Work Adjustment Training",
      community_integration: "Community Integration",
      career_development: "Career Development",
      transition_services: "Transition Services",
    },
    "Case Management & Support": {
      case_management: "Case Management",
      progress_monitoring: "Progress Monitoring",
      family_coordination: "Family Coordination",
      advocacy_support: "Advocacy & Support",
    },
    "Administrative Functions": {
      compliance_review: "Compliance Review",
      reporting: "Program Reporting",
      audit: "Audit & Evaluation",
      training: "Staff Training",
    },
    "Emergency & Crisis": {
      emergency: "Emergency Access",
      crisis_intervention: "Crisis Intervention",
    },
  };

  // ✅ Add validation helper
  const validatePurpose = (purpose) => {
    const validPurposes = Object.keys(purposeDescriptions);
    if (!validPurposes.includes(purpose)) {
      console.warn(
        `Invalid purpose: ${purpose}. Valid options:`,
        validPurposes
      );
      return false;
    }
    return true;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.reason.trim() || formData.reason.length < 10) {
      newErrors.reason =
        "Please provide a detailed business reason (minimum 10 characters)";
    }

    if (!formData.acknowledgment) {
      newErrors.acknowledgment =
        "You must acknowledge confidentiality and compliance requirements";
    }

    // ✅ Add purpose validation
    if (!validatePurpose(formData.purpose)) {
      newErrors.purpose = "Please select a valid purpose for access";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Clear stored data on successful submission
    clearStoredData();

    // Pass the form data to the onConfirm callback
    onConfirm({
      reason: formData.reason,
      purpose: formData.purpose,
      emergencyAccess: formData.emergencyAccess,
      acknowledgment: formData.acknowledgment,
      timestamp: new Date().toISOString(),
    });
  };

  const handleCancel = () => {
    // Clear stored data on cancel
    clearStoredData();
    onCancel();
  };

  const handleFormDataChange = (updates) => {
    setFormData((prev) => ({ ...prev, ...updates }));
    // Clear purpose error when user selects a new purpose
    if (updates.purpose && errors.purpose) {
      setErrors((prev) => ({ ...prev, purpose: undefined }));
    }
  };

  return (
    <div
      className="modal show d-block"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header bg-primary">
            <h5 className="modal-title text-white">
              <i className="bi bi-shield-check me-2"></i>
              Consumer Information Access Authorization
            </h5>
            {/* 🔧 Visual indicator that form data is preserved */}
            {(formData.reason ||
              formData.purpose !== "vocational_services") && (
              <small className="text-light">
                <i className="bi bi-check-circle-fill text-success me-1"></i>
                Form data preserved
              </small>
            )}
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {/* Privacy & Confidentiality Notice */}
              <div className="alert alert-info" role="alert">
                <h6 className="alert-heading">
                  <i className="bi bi-info-circle-fill me-2"></i>
                  Consumer Information Privacy Notice
                </h6>
                <p className="mb-2">
                  You are requesting access to confidential consumer
                  information.
                </p>
                <p className="mb-0">
                  <strong>
                    Access must be justified with a legitimate vocational
                    rehabilitation purpose.
                  </strong>
                </p>
              </div>

              {/* ✅ Enhanced Purpose Selection for VR/CRP Services */}
              <div className="mb-3">
                <label htmlFor="purpose" className="form-label">
                  <strong>Purpose of Access</strong>{" "}
                  <span className="text-danger">*</span>
                </label>
                <select
                  className={`form-select ${
                    errors.purpose ? "is-invalid" : ""
                  }`}
                  id="purpose"
                  value={formData.purpose}
                  onChange={(e) => {
                    const newPurpose = e.target.value;
                    if (validatePurpose(newPurpose)) {
                      handleFormDataChange({ purpose: newPurpose });
                    }
                  }}
                  required
                >
                  <option value="">Select service purpose...</option>
                  {Object.entries(purposeGroups).map(
                    ([groupName, purposes]) => (
                      <optgroup key={groupName} label={groupName}>
                        {Object.entries(purposes).map(([key, label]) => (
                          <option key={key} value={key}>
                            {label}
                          </option>
                        ))}
                      </optgroup>
                    )
                  )}
                </select>
                {errors.purpose && (
                  <div className="invalid-feedback">{errors.purpose}</div>
                )}
                <div className="form-text">
                  {formData.purpose && purposeDescriptions[formData.purpose]
                    ? purposeDescriptions[formData.purpose]
                    : "Select a purpose to see its description"}
                </div>
              </div>

              {/* Business Reason */}
              <div className="mb-3">
                <label htmlFor="reason" className="form-label">
                  <strong>Specific Reason for Access</strong>{" "}
                  <span className="text-danger">*</span>
                </label>
                <textarea
                  className={`form-control ${
                    errors.reason ? "is-invalid" : ""
                  }`}
                  id="reason"
                  rows="3"
                  value={formData.reason}
                  onChange={(e) =>
                    handleFormDataChange({ reason: e.target.value })
                  }
                  placeholder="Provide a detailed explanation of why you need access to consumer information (minimum 10 characters)"
                  required
                />
                {errors.reason && (
                  <div className="invalid-feedback">{errors.reason}</div>
                )}
                <div className="form-text">
                  Be specific about your job duties requiring consumer
                  information access
                </div>
              </div>

              {/* Emergency Access */}
              <div className="mb-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="emergencyAccess"
                    checked={formData.emergencyAccess}
                    onChange={(e) =>
                      handleFormDataChange({
                        emergencyAccess: e.target.checked,
                      })
                    }
                  />
                  <label className="form-check-label" htmlFor="emergencyAccess">
                    <strong>Emergency/Crisis Access</strong>
                    <div className="small text-muted mt-1">
                      Check this only for urgent situations requiring immediate
                      access to consumer information
                    </div>
                  </label>
                </div>
              </div>

              {/* Confidentiality Acknowledgment */}
              <div className="mb-3">
                <div className="form-check">
                  <input
                    className={`form-check-input ${
                      errors.acknowledgment ? "is-invalid" : ""
                    }`}
                    type="checkbox"
                    id="acknowledgment"
                    checked={formData.acknowledgment}
                    onChange={(e) =>
                      handleFormDataChange({ acknowledgment: e.target.checked })
                    }
                    required
                  />
                  <label className="form-check-label" htmlFor="acknowledgment">
                    <strong>Confidentiality & Compliance Acknowledgment</strong>
                    <div className="small mt-1">
                      I acknowledge that:
                      <ul className="mb-0 mt-1">
                        <li>
                          I am authorized to access this consumer information
                          for my job duties in vocational rehabilitation
                          services
                        </li>
                        <li>
                          I understand my obligations to protect consumer
                          confidentiality and privacy under state and federal
                          regulations
                        </li>
                        <li>
                          I have received appropriate training on consumer
                          information handling and confidentiality requirements
                        </li>
                        <li>
                          I will comply with all privacy and security policies
                        </li>
                        <li>
                          I will access only the minimum necessary information
                          to perform my job duties
                        </li>
                        <li>
                          I will not share, discuss, or disclose consumer
                          information except as permitted by policy and law
                        </li>
                        <li>
                          I understand that violations may result in
                          disciplinary action, termination, and/or legal
                          penalties
                        </li>
                        <li>
                          This access will be logged and may be audited for
                          compliance with vocational rehabilitation standards
                        </li>
                      </ul>
                    </div>
                  </label>
                  {errors.acknowledgment && (
                    <div className="invalid-feedback d-block">
                      {errors.acknowledgment}
                    </div>
                  )}
                </div>
              </div>

              {/* Record Information */}
              <div className="alert alert-secondary">
                <small>
                  <strong>Consumer Record:</strong> {entityType} (ID: {entityId}
                  )
                  <br />
                  <strong>Timestamp:</strong> {new Date().toLocaleString()}
                  <br />
                  <strong>User Session:</strong> This access authorization will
                  be valid 90 Days
                  <br />
                </small>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleCancel}
              >
                <i className="bi bi-x-circle me-1"></i>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!formData.acknowledgment}
              >
                <i className="bi bi-check-circle me-1"></i>
                Authorize Access
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PHIAccessModal;
